/**
 * Homepage Vehicle Filter
 * Handles the collapsible vehicle filter form on the homepage
 */
class HomepageVehicleFilter {
  constructor() {
    this.section = document.querySelector('.homepage-vehicle-filter-section');
    if (!this.section) return;

    this.wrapper = this.section.querySelector('.homepage-vehicle-filter__wrapper');
    if (!this.wrapper) return;

    this.toggleBtn = this.wrapper.querySelector('.homepage-vehicle-filter__toggle');
    this.toggleBtnFixed = this.section.querySelector('.homepage-vehicle-filter__toggle-btn');
    this.closeBtn = this.wrapper.querySelector('.homepage-vehicle-filter__close');
    this.content = this.wrapper.querySelector('.homepage-vehicle-filter__content');
    
    this.isOpen = !this.wrapper.classList.contains('homepage-vehicle-filter__wrapper--closed');
    
    // Set initial display state
    if (!this.isOpen) {
      this.wrapper.style.display = 'none';
    } else {
      this.wrapper.style.display = 'flex';
    }
    
    this.init();
  }

  init() {
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => this.toggle());
    }
    
    if (this.toggleBtnFixed) {
      this.toggleBtnFixed.addEventListener('click', () => this.open());
    }
    
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    
    // Initialize the vehicle filter API with homepage-specific IDs
    this.initVehicleFilterAPI();
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.updateState();
  }

  open() {
    this.isOpen = true;
    this.updateState();
  }

  close() {
    this.isOpen = false;
    this.updateState();
  }

  updateState() {
    if (this.isOpen) {
      // Opening: Set display first, then animate opacity
      this.wrapper.style.display = 'flex';
      // Force reflow to ensure display change is applied
      this.wrapper.offsetHeight;
      // Remove closed class to trigger opacity animation
      this.wrapper.classList.remove('homepage-vehicle-filter__wrapper--closed');
      // Hide toggle button when opening
      if (this.toggleBtnFixed) {
        this.toggleBtnFixed.style.display = 'none';
      }
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'true');
      }
    } else {
      // Closing: Animate opacity first, then hide
      this.wrapper.classList.add('homepage-vehicle-filter__wrapper--closed');
      // Show toggle button when closing
      if (this.toggleBtnFixed) {
        this.toggleBtnFixed.style.display = 'flex';
      }
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'false');
      }      
        if (!this.isOpen) {
          this.wrapper.style.display = 'none';
        }
    }
  }

  initVehicleFilterAPI() {
    // Initialize the homepage-specific vehicle filter API
    // This is self-contained and doesn't depend on VehicleFilterAPI
    this.vehicleFilter = new HomepageVehicleFilterAPI();
  }
}

/**
 * Homepage-specific Vehicle Filter API
 * Extends the base VehicleFilterAPI functionality for homepage use
 */
class HomepageVehicleFilterAPI {
  constructor() {
    this.form = document.querySelector('.homepage-vehicle-filter-form');
    if (!this.form) return;

    this.apiUrl = this.form.dataset.apiUrl;
    this.makeSelect = document.getElementById('homepage-vehicle-make');
    this.modelSelect = document.getElementById('homepage-vehicle-model');
    this.yearSelect = document.getElementById('homepage-vehicle-year');
    this.submitBtn = document.getElementById('homepage-vehicle-filter-submit');
    this.loadingEl = this.form.querySelector('.homepage-vehicle-filter-form__loading');

    this.selectedMake = null;
    this.selectedModel = null;
    this.selectedYear = null;

    this.isFiltering = false;
    
    // Homepage always redirects to collection
    this.isProductPage = false;
    
    // CSV data structure
    this.csvUrl = 'https://cdn.shopify.com/s/files/1/0778/4038/4259/files/makemodelyear.csv?v=1767716396';
    this.vehicleData = {
      makes: new Set(),
      modelsByMake: {}, // { make: Set of models }
      yearsByMakeModel: {} // { make: { model: Set of years } }
    };
    
    // Collection product data structure (for filtering by collection)
    this.collectionData = {
      makes: new Set(),
      modelsByMake: {}, // { make: Set of models }
      yearsByMakeModel: {} // { make: { model: Set of years } }
    };
    this.isCollectionPage = false;
    this.collectionHandle = null;

    this.init();
  }

  async init() {
    await this.loadCSVData();
    await this.loadCollectionData();
    await this.loadMakes();
    this.attachEventListeners();
  }
  
  async loadCollectionData() {
    // Check if we're on a collection page
    const pathMatch = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
    if (!pathMatch) {
      // Not on a collection page, skip collection filtering
      return;
    }
    
    this.isCollectionPage = true;
    this.collectionHandle = pathMatch[1];
    
    // Skip 'all' collection - treat it as not a specific collection
    if (this.collectionHandle === 'all') {
      this.isCollectionPage = false;
      return;
    }
    
    this.showLoading(true);
    try {
      
      // Fetch all products from collection (handle pagination)
      // Template paginates by 250, so we may need multiple requests
      let allProducts = [];
      let page = 1;
      let hasMore = true;
      const maxPages = 10; // Safety limit
      
      while (hasMore && page <= maxPages) {
        const url = page === 1 
          ? `/collections/${this.collectionHandle}?view=vehicle-data`
          : `/collections/${this.collectionHandle}?view=vehicle-data&page=${page}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          console.warn(`Failed to fetch collection products page ${page}: ${response.status}`);
          break;
        }
        
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn(`Response is not JSON for page ${page}, stopping pagination`);
          // If first page isn't JSON, the template might not exist
          if (page === 1) {
            console.warn('vehicle-data template may not exist for this collection');
          }
          break;
        }
        
        let products;
        try {
          products = await response.json();
        } catch (error) {
          console.error(`Failed to parse JSON for page ${page}:`, error);
          break;
        }
        
        if (!products || !Array.isArray(products) || products.length === 0) {
          hasMore = false;
        } else {
          allProducts = allProducts.concat(products);
          // Template paginates by 250, so if we get less, we're done
          if (products.length < 250) {
            hasMore = false;
          } else {
            page++;
          }
        }
      }
      
      
      // Extract vehicle data from collection products
      allProducts.forEach(product => {
        const make = this.extractValue(product.make);
        const model = this.extractValue(product.model);
        const year = this.extractValue(product.year);
        
        // Handle arrays (list-type metafields) - process all combinations
        const makes = Array.isArray(make) ? make : (make ? [make] : []);
        const models = Array.isArray(model) ? model : (model ? [model] : []);
        const years = Array.isArray(year) ? year : (year ? [year] : []);
        
        // If no make, skip this product
        if (makes.length === 0) return;
        
        // Process all combinations of make/model/year
        makes.forEach(m => {
          const makeStr = String(m).trim();
          if (!makeStr) return;
          
          this.collectionData.makes.add(makeStr);
          
          if (models.length > 0) {
            models.forEach(mod => {
              const modelStr = String(mod).trim();
              if (!modelStr) return;
              
              if (!this.collectionData.modelsByMake[makeStr]) {
                this.collectionData.modelsByMake[makeStr] = new Set();
              }
              this.collectionData.modelsByMake[makeStr].add(modelStr);
              
              if (years.length > 0) {
                years.forEach(y => {
                  const yearStr = String(y).trim();
                  if (!yearStr) return;
                  
                  if (!this.collectionData.yearsByMakeModel[makeStr]) {
                    this.collectionData.yearsByMakeModel[makeStr] = {};
                  }
                  if (!this.collectionData.yearsByMakeModel[makeStr][modelStr]) {
                    this.collectionData.yearsByMakeModel[makeStr][modelStr] = new Set();
                  }
                  this.collectionData.yearsByMakeModel[makeStr][modelStr].add(yearStr);
                });
              }
            });
          }
        });
      });
      
    } catch (error) {
      console.error('Error loading collection data:', error);
    } finally {
      this.showLoading(false);
    }
  }
  
  extractValue(value) {
    // Handle both array and string values
    // For arrays, we need to process all values (not just first)
    // But for building the filter structure, we'll use all values
    if (!value) return null;
    
    if (Array.isArray(value)) {
      // Return array of non-null values
      return value.filter(v => v != null && v !== '');
    }
    
    return value;
  }
  
  async loadCSVData() {
    this.showLoading(true);
    try {
      const response = await fetch(this.csvUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status}`);
      }
      
      const csvText = await response.text();
      this.parseCSV(csvText);
    } catch (error) {
      console.error('Error loading CSV data:', error);
      this.showError(this.makeSelect, 'Error loading vehicle data');
    } finally {
      this.showLoading(false);
    }
  }
  
  parseCSV(csvText) {
    const lines = csvText.trim().split('\n');
    
    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Parse CSV line (handling quoted values)
      const values = this.parseCSVLine(line);
      if (values.length < 4) continue;
      
      const [, make, model, year] = values;
      
      if (!make || !model || !year) continue;
      
      // Normalize values (trim whitespace)
      const normalizedMake = make.trim();
      const normalizedModel = model.trim();
      const normalizedYear = year.trim();
      
      // Add to data structure
      this.vehicleData.makes.add(normalizedMake);
      
      // Initialize modelsByMake if needed
      if (!this.vehicleData.modelsByMake[normalizedMake]) {
        this.vehicleData.modelsByMake[normalizedMake] = new Set();
      }
      this.vehicleData.modelsByMake[normalizedMake].add(normalizedModel);
      
      // Initialize yearsByMakeModel if needed
      if (!this.vehicleData.yearsByMakeModel[normalizedMake]) {
        this.vehicleData.yearsByMakeModel[normalizedMake] = {};
      }
      if (!this.vehicleData.yearsByMakeModel[normalizedMake][normalizedModel]) {
        this.vehicleData.yearsByMakeModel[normalizedMake][normalizedModel] = new Set();
      }
      this.vehicleData.yearsByMakeModel[normalizedMake][normalizedModel].add(normalizedYear);
    }
  }
  
  parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current); // Push last value
    
    return values;
  }

  attachEventListeners() {
    this.makeSelect.addEventListener('change', async (e) => {
      this.selectedMake = e.target.value;
      this.selectedModel = null;
      this.selectedYear = null;

      if (this.selectedMake) {
        await this.loadModels();
      } else {
        this.resetModelSelect();
        this.resetYearSelect();
      }
      this.updateSubmitButtonState();
    });

    this.modelSelect.addEventListener('change', async (e) => {
      this.selectedModel = e.target.value;
      this.selectedYear = null;

      if (this.selectedModel) {
        await this.loadYears();
      } else {
        this.resetYearSelect();
      }
      this.updateSubmitButtonState();
    });

    this.yearSelect.addEventListener('change', (e) => {
      this.selectedYear = e.target.value;
      this.updateSubmitButtonState();
    });

    this.submitBtn.addEventListener('click', () => {
      this.handleSubmit();
    });
  }
  
  updateSubmitButtonState() {
    // Enable submit button only if all three fields are selected
    const allFieldsSelected = this.selectedMake && this.selectedModel && this.selectedYear;
    this.submitBtn.disabled = !allFieldsSelected;
  }

  async loadMakes() {
    this.showLoading(true);
    try {
      // Fetch makes from API
      const makesData = await this.fetchFilters();
      
      if (!makesData.success || !makesData.data.makes || makesData.data.makes.length === 0) {
        this.showError(this.makeSelect, 'No makes available');
        return;
      }

      // Filter makes: must exist in CSV AND (collection if on collection page)
      let validMakes = makesData.data.makes.filter(make => 
        this.vehicleData.makes.has(make.trim())
      );
      
      // If on collection page, ONLY show makes that exist in collection products
      if (this.isCollectionPage) {
        if (this.collectionData.makes.size === 0) {
          // Collection has no products with vehicle data, show no makes
          validMakes = [];
        } else {
          validMakes = validMakes.filter(make => 
            this.collectionData.makes.has(make.trim())
          );
        }
      }

      if (validMakes.length > 0) {
        this.populateSelect(this.makeSelect, validMakes.sort(), 'Select Make');
        this.makeSelect.disabled = false;
      } else {
        this.showError(this.makeSelect, 'No valid makes available');
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      this.showError(this.makeSelect, 'Error loading makes');
    } finally {
      this.showLoading(false);
    }
  }

  async loadModels() {
    this.showLoading(true);
    try {
      if (!this.selectedMake) {
        this.resetModelSelect();
        return;
      }
      
      // Fetch models from API
      const modelsData = await this.fetchFilters({ make: this.selectedMake });
      
      if (!modelsData.success || !modelsData.data.models || modelsData.data.models.length === 0) {
        this.showError(this.modelSelect, 'No models available');
        this.resetYearSelect();
        return;
      }

      // Filter models: must exist in CSV for the selected make
      const csvModelsSet = this.vehicleData.modelsByMake[this.selectedMake];
      let validModels = modelsData.data.models.filter(model => 
        csvModelsSet && csvModelsSet.has(model.trim())
      );
      
      // If on collection page, ONLY show models that exist in collection products
      if (this.isCollectionPage) {
        const collectionModelsSet = this.collectionData.modelsByMake[this.selectedMake];
        if (!collectionModelsSet || collectionModelsSet.size === 0) {
          // Collection has no models for this make, show no models
          validModels = [];
        } else {
          validModels = validModels.filter(model => 
            collectionModelsSet.has(model.trim())
          );
        }
      }

      if (validModels.length > 0) {
        this.populateSelect(this.modelSelect, validModels.sort(), 'Select Model');
        this.modelSelect.disabled = false;
        
        // Reset year select since model changed
        this.resetYearSelect();
      } else {
        this.showError(this.modelSelect, 'No valid models available for this make');
        this.resetYearSelect();
      }
    } catch (error) {
      console.error('Error loading models:', error);
      this.showError(this.modelSelect, 'Error loading models');
      this.resetYearSelect();
    } finally {
      this.showLoading(false);
    }
  }

  async loadYears() {
    this.showLoading(true);
    try {
      if (!this.selectedMake || !this.selectedModel) {
        this.resetYearSelect();
        return;
      }
      
      // Fetch years from API
      const data = await this.fetchFilters({ 
        make: this.selectedMake, 
        model: this.selectedModel 
      });
      
      if (!data.success || !data.data.years || data.data.years.length === 0) {
        this.showError(this.yearSelect, 'No years available');
        return;
      }
      
      // Filter years: must exist in CSV for the selected make+model
      const csvYearsSet = this.vehicleData.yearsByMakeModel[this.selectedMake]?.[this.selectedModel];
      let validYears = data.data.years.filter(year => 
        csvYearsSet && csvYearsSet.has(year.trim())
      );
      
      // If on collection page, ONLY show years that exist in collection products
      if (this.isCollectionPage) {
        const collectionYearsSet = this.collectionData.yearsByMakeModel[this.selectedMake]?.[this.selectedModel];
        if (!collectionYearsSet || collectionYearsSet.size === 0) {
          // Collection has no years for this make+model, show no years
          validYears = [];
        } else {
          validYears = validYears.filter(year => 
            collectionYearsSet.has(year.trim())
          );
        }
      }
      
      if (validYears.length > 0) {
        // Sort years: numeric years descending, then non-numeric
        const numericYears = validYears
          .map(year => parseInt(year.trim(), 10))
          .filter(year => !isNaN(year))
          .sort((a, b) => b - a)
          .map(year => year.toString());
        
        const nonNumericYears = validYears
          .filter(year => isNaN(parseInt(year.trim(), 10)))
          .sort();
        
        const allYears = [...numericYears, ...nonNumericYears];
        
        this.populateSelect(this.yearSelect, allYears, 'Select Year');
        this.yearSelect.disabled = false;
      } else {
        this.showError(this.yearSelect, 'No valid years available for this model');
      }
    } catch (error) {
      console.error('Error loading years:', error);
      this.showError(this.yearSelect, 'Error loading years');
    } finally {
      this.showLoading(false);
    }
  }

  async fetchFilters(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `${this.apiUrl}/api/vehicle-filters${queryString ? '?' + queryString : ''}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }
    
    return await response.json();
  }

  populateSelect(select, options, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>`;
    options.forEach(option => {
      const optionEl = document.createElement('option');
      optionEl.value = option;
      optionEl.textContent = option;
      select.appendChild(optionEl);
    });
  }

  resetModelSelect() {
    this.modelSelect.innerHTML = '<option value="">Select Model</option>';
    this.modelSelect.disabled = true;
  }

  resetYearSelect() {
    this.yearSelect.innerHTML = '<option value="">Select Year</option>';
    this.yearSelect.disabled = true;
  }

  showError(select, message) {
    select.innerHTML = `<option value="">${message}</option>`;
    select.disabled = true;
  }

  showLoading(show) {
    if (this.loadingEl) {
      this.loadingEl.style.display = show ? 'block' : 'none';
    }
  }

  async handleSubmit() {
    if (!this.selectedMake || !this.selectedModel || !this.selectedYear) {
      alert('Please select Make, Model, and Year');
      return;
    }

    // Always redirect to collection page with filters (homepage behavior)
    this.redirectToCollectionWithFilters();
  }
  
  redirectToCollectionWithFilters() {
    // Build filter URL parameters
    const filterParams = new URLSearchParams();
    
    if (this.selectedMake) {
      filterParams.append('filter.p.m.magento.make_filter', this.selectedMake);
    }
    if (this.selectedModel) {
      filterParams.append('filter.p.m.magento.model_filter', this.selectedModel);
    }
    if (this.selectedYear) {
      filterParams.append('filter.p.m.magento.year_filter', this.selectedYear);
    }
    
    // If we're on a collection page, stay on that collection and apply filters
    // Otherwise, redirect to /collections/all
    if (this.isCollectionPage && this.collectionHandle) {
      const basePath = `/collections/${this.collectionHandle}`;
      const fullUrl = filterParams.toString() 
        ? `${basePath}?${filterParams.toString()}`
        : basePath;
      
      window.location.href = fullUrl;
    } else {
      // Not on a collection page, redirect to all products collection with filters
      const collectionUrl = '/collections/all';
      const fullUrl = filterParams.toString() 
        ? `${collectionUrl}?${filterParams.toString()}`
        : collectionUrl;
      
      window.location.href = fullUrl;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HomepageVehicleFilter();
});

